// Step 4 — AI demo on the user's real data (US-023, US-024, US-025, US-026).
//
// Pixel-faithful to `prototypes_and_docs/ls-onboarding-v2.jsx:403-777`.
//
import { ArrowRight, ChevronLeft, RefreshCw, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  useOnboardingAiDemo,
  useRetryOnboardingAiDemo,
  type OnboardingAiDemoConversation,
  type OnboardingAiDemoInsight,
  type OnboardingAiDemoMessage,
  type OnboardingAiDemoProgress,
  type OnboardingAiDemoSignal,
  type OnboardingAiDemoSuggestion,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Card, OnboardingStep, ProgressBar } from '@barel/ui';

import type { OnboardingGoal, DefaultInboxMode } from '@barel/core';

import { formatRelativeTime } from '@/lib/formatRelativeTime';

interface AIDemoProps {
  goal: OnboardingGoal | null;
  inboxMode: DefaultInboxMode | null;
  onBack: () => void;
  onContinue: () => void;
}

type DemoPhase = 'scrape' | 'profile' | 'ai';

// Pre-data / silent-0% fallback shown before the first poll resolves.
const FALLBACK_PROGRESS: OnboardingAiDemoProgress = {
  percent: 0,
  threadsScanned: 0,
  messagesScanned: 0,
  totalEstimated: 10,
  complete: false,
  updatedAt: null,
};

// Idle-at-0% thresholds. Below the short one we keep the normal progress card
// (avoids flicker when the extension delivers within a couple of polls). Past
// it we explain the silence; past the long one we surface a clear escape hatch.
const WAITING_HINT_MS = 12_000;
const LONG_WAIT_MS = 45_000;

/**
 * Ticks once per second while `active`, returning elapsed milliseconds since it
 * last became active (0 when inactive). Counts ticks rather than reading
 * `Date.now()` so the threshold logic stays deterministic under fake timers.
 */
function useElapsedMs(active: boolean): number {
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!active) {
      setElapsedMs(0);
      return;
    }
    const id = setInterval(() => setElapsedMs((ms) => ms + 1000), 1000);
    return () => clearInterval(id);
  }, [active]);
  return active ? elapsedMs : 0;
}

export function AIDemo({ goal, inboxMode, onBack, onContinue }: AIDemoProps) {
  const t = useT('onboarding');
  const [phase, setPhase] = useState<DemoPhase>('scrape');
  const demo = useOnboardingAiDemo({ refetchInterval: 2500 });
  const retryDemo = useRetryOnboardingAiDemo();

  useEffect(() => {
    if (demo.data?.status !== 'ready') {
      setPhase('scrape');
      return;
    }
    setPhase('profile');
    const timer = setTimeout(() => setPhase('ai'), 1100);
    return () => clearTimeout(timer);
  }, [demo.data?.status, demo.data?.conversation?.id]);

  const isCampaignsOnly = goal === 'campaigns';
  const data = demo.data;
  const showBonusBanner = isCampaignsOnly && data?.status !== 'empty';

  // "Scanning" = any pre-reveal state rendered through the progress card:
  // initial load, backfilling, or ready-but-not-yet-hydrated. Excludes
  // empty/failed/transport-error, which have their own dedicated states below.
  const scanProgress = data?.progress ?? FALLBACK_PROGRESS;
  const signalsCount = data?.signals.length ?? 0;
  const isScanning =
    !demo.isError &&
    (!data ||
      (data.status !== 'empty' &&
        data.status !== 'failed' &&
        (data.status === 'backfilling' || !data.conversation || !data.suggestion)));
  // Silent 0% — the extension has not reported anything back yet.
  const idleAtZero =
    isScanning &&
    scanProgress.percent === 0 &&
    scanProgress.threadsScanned === 0 &&
    scanProgress.messagesScanned === 0 &&
    signalsCount === 0;
  const idleElapsedMs = useElapsedMs(idleAtZero);
  const showWaitingHint = idleAtZero && idleElapsedMs >= WAITING_HINT_MS;
  const showLongWait = idleAtZero && idleElapsedMs >= LONG_WAIT_MS;

  return (
    <OnboardingStep
      width="lg"
      eyebrow={isCampaignsOnly ? t('step4.eyebrow.campaigns') : t('step4.eyebrow.default')}
      title={isCampaignsOnly ? t('step4.title.campaigns') : t('step4.title.default')}
      description={isCampaignsOnly ? t('step4.subtitle.campaigns') : t('step4.subtitle.default')}
      primary={
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={onContinue}
          iconRight={<ArrowRight aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step4-continue"
        >
          {t('wizard.continue')}
        </Button>
      }
      secondary={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step4-back"
        >
          {t('wizard.back')}
        </Button>
      }
    >
      {showBonusBanner ? (
        <Banner tone="accent" variant="gradient">
          <strong>{t('step4.bonusBanner.title')}</strong> {t('step4.bonusBanner.body')}
        </Banner>
      ) : null}

      {demo.isError ? (
        <FailedDemo reason={t('step4.failed.network')} onRetry={() => void demo.refetch()} />
      ) : !data ? (
        <ScanningView
          progress={FALLBACK_PROGRESS}
          signalsCount={0}
          waiting={showWaitingHint}
          longWait={showLongWait}
          onContinue={onContinue}
        />
      ) : data.status === 'empty' ? (
        <EmptyDemo />
      ) : data.status === 'failed' ? (
        <FailedDemo
          reason={failureCopy(t, data.reason)}
          onRetry={() => retryDemo.mutate()}
          retrying={retryDemo.isPending}
        />
      ) : data.status === 'backfilling' || !data.conversation || !data.suggestion ? (
        <ScanningView
          progress={data.progress}
          signalsCount={data.signals.length}
          waiting={showWaitingHint}
          longWait={showLongWait}
          onContinue={onContinue}
        />
      ) : (
        <DemoReveal
          conversation={data.conversation}
          suggestion={data.suggestion}
          insight={data.insight}
          signals={data.signals}
          phase={phase}
          inboxMode={inboxMode}
        />
      )}
    </OnboardingStep>
  );
}

function failureCopy(t: ReturnType<typeof useT>, reason: string | null): string {
  if (reason === 'rate_limited') return t('step4.failed.rateLimited');
  if (reason === 'no_account') return t('step4.failed.noAccount');
  if (reason === 'no_messaging_tab') return t('step4.failed.noMessagingTab');
  if (reason === 'extension_inactive') return t('step4.failed.extensionInactive');
  if (reason === 'voyager_shape') return t('step4.failed.voyagerShape');
  if (reason === 'linkedin_checkpoint') return t('step4.failed.linkedinCheckpoint');
  if (reason === 'linkedin_auth') return t('step4.failed.linkedinAuth');
  if (reason === 'voyager_error') return t('step4.failed.voyagerError');
  return t('step4.failed.body');
}

// Chooses between the silent-0% "waiting on the extension" hint and the normal
// progress card. Keeps the happy path (any actual progress) visually unchanged.
function ScanningView({
  progress,
  signalsCount,
  waiting,
  longWait,
  onContinue,
}: {
  progress: OnboardingAiDemoProgress;
  signalsCount: number;
  waiting: boolean;
  longWait: boolean;
  onContinue: () => void;
}) {
  if (waiting) {
    return <WaitingForExtension longWait={longWait} onContinue={onContinue} />;
  }
  return <ScrapeProgress progress={progress} signalsCount={signalsCount} />;
}

function WaitingForExtension({
  longWait,
  onContinue,
}: {
  longWait: boolean;
  onContinue: () => void;
}) {
  const t = useT('onboarding');
  return (
    <Card padding="lg" radius="xl" data-region="onboarding-scrape-waiting">
      <div className="flex flex-col items-center gap-3 text-center">
        <Sparkles className="text-accent" style={{ width: 28, height: 28 }} aria-hidden />
        <span className="text-sm font-bold text-primary">{t('step4.waiting.title')}</span>
        <span className="max-w-sm text-xs leading-relaxed text-secondary">
          {t('step4.waiting.body')}
        </span>
        {longWait ? (
          <div className="flex flex-col items-center gap-2 border-t border-default pt-3">
            <span className="max-w-sm text-2xs leading-relaxed text-tertiary">
              {t('step4.waiting.longBody')}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onContinue}
              data-testid="onboarding-step4-later"
            >
              {t('step4.waiting.later')}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function ScrapeProgress({
  progress,
  signalsCount,
}: {
  progress: OnboardingAiDemoProgress;
  signalsCount: number;
}) {
  const t = useT('onboarding');
  let phaseLabel: string;
  if (progress.percent < 35) phaseLabel = t('step4.scrape.phaseScan');
  else if (progress.percent < 75) phaseLabel = t('step4.scrape.phaseProfiles');
  else if (progress.percent < 100) phaseLabel = t('step4.scrape.phaseSelect');
  else phaseLabel = t('step4.scrape.phaseFinalise');

  const counters = [
    { key: 'scanned', val: progress.threadsScanned },
    { key: 'profiles', val: progress.messagesScanned },
    { key: 'signals', val: signalsCount },
  ] as const;

  return (
    <Card padding="lg" radius="xl" data-region="onboarding-scrape">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-accent-border bg-accent-dim text-accent"
          >
            <Sparkles style={{ width: 16, height: 16 }} />
          </span>
          <div className="flex flex-1 flex-col">
            <span className="text-sm font-bold text-primary">{t('step4.scrape.title')}</span>
            <span className="text-2xs text-secondary">{phaseLabel}</span>
          </div>
          <span className="text-lg font-bold text-accent">{Math.round(progress.percent)}%</span>
        </div>
        <ProgressBar value={progress.percent} max={100} aria-label={t('step4.scrape.title')} />
        <div className="grid grid-cols-3 gap-2 border-t border-default pt-3">
          {counters.map((c) => {
            return (
              <div
                key={c.key}
                className="flex flex-col items-center rounded-md border border-default bg-bg-surface p-2"
              >
                <span className="text-base font-bold tabular-nums text-primary">{c.val}</span>
                <span className="text-2xs font-semibold uppercase tracking-wide text-tertiary">
                  {t(`step4.scrape.counter.${c.key}`)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function DemoReveal({
  conversation,
  suggestion,
  insight,
  signals,
  phase,
  inboxMode,
}: {
  conversation: OnboardingAiDemoConversation;
  suggestion: OnboardingAiDemoSuggestion;
  insight: OnboardingAiDemoInsight | null;
  signals: OnboardingAiDemoSignal[];
  phase: DemoPhase;
  inboxMode: DefaultInboxMode | null;
}) {
  const t = useT('onboarding');
  const showAi = phase === 'ai';
  return (
    <div className="flex flex-col gap-3">
      <Card padding="md" radius="xl" data-region="onboarding-profile">
        <div className="flex flex-col gap-3">
          <span className="text-2xs font-bold uppercase tracking-wider text-tertiary">
            {!insight?.personalized
              ? t('step4.profile.fallbackEyebrow')
              : insight.isHot
                ? t('step4.profile.hotEyebrow')
                : t('step4.profile.matchEyebrow')}
          </span>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-dim text-sm font-bold text-accent"
            >
              {conversation.contactInitials}
            </span>
            <div className="flex flex-1 flex-col">
              <span className="text-sm font-bold text-primary">{conversation.contactName}</span>
              <span className="text-xs text-secondary">
                {conversation.contactRole ?? t('step4.profile.unknownRole')}
              </span>
              <span className="text-2xs text-tertiary">
                {conversation.contactCompany ?? t('step4.profile.linkedinThread')}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 border-t border-default pt-3">
            <span className="text-2xs font-bold uppercase tracking-wider text-tertiary">
              {t('step4.profile.threadEyebrow')}
            </span>
            {conversation.messages.map((m) => (
              <ThreadBubble
                key={m.id}
                message={m}
                leadName={conversation.contactName.split(' ')[0] ?? ''}
              />
            ))}
            {showAi && inboxMode === 'auto' ? (
              <AutoSentBubble text={suggestion.body} insight={insight} />
            ) : showAi ? (
              <AiSuggestionBubble text={suggestion.body} insight={insight} />
            ) : null}
          </div>
        </div>
      </Card>

      {showAi ? (
        <div className="flex flex-col gap-2">
          <span className="text-2xs font-bold uppercase tracking-wider text-tertiary">
            {t('step4.signals.eyebrow')}
          </span>
          {signals.map((s) => (
            <Card key={s.label} padding="sm" radius="lg">
              <div className="flex items-center gap-3">
                <span className="min-w-28 text-2xs font-bold uppercase tracking-wider text-tertiary">
                  {s.label}
                </span>
                <span className="flex-1 text-xs text-primary">{s.value}</span>
              </div>
            </Card>
          ))}
          <Banner tone="info">{t('step4.tip')}</Banner>
        </div>
      ) : null}
    </div>
  );
}

function ThreadBubble({
  message,
  leadName,
}: {
  message: OnboardingAiDemoMessage;
  leadName: string;
}) {
  const t = useT('onboarding');
  const isOut = message.role === 'out';
  return (
    <div
      className={'flex ' + (isOut ? 'justify-end' : 'justify-start')}
      data-direction={isOut ? 'out' : 'in'}
    >
      <div
        data-highlight={message.highlight || undefined}
        className={
          'max-w-[82%] rounded-md border px-3 py-2 text-xs leading-relaxed ' +
          (isOut
            ? 'rounded-tr-sm border-accent-border bg-accent-dim text-primary'
            : 'rounded-tl-sm border-default bg-bg-surface text-primary') +
          (message.highlight ? ' border-l-4 border-l-warning' : '')
        }
      >
        <div className="text-2xs font-semibold uppercase tracking-wide text-tertiary">
          {isOut ? t('step4.thread.you') : leadName} · {formatRelativeTime(message.at)}
        </div>
        <div className="mt-1 text-xs">{message.text}</div>
      </div>
    </div>
  );
}

function PersonalizationNote({ insight }: { insight: OnboardingAiDemoInsight | null }) {
  const t = useT('onboarding');
  return (
    <span className="text-2xs text-tertiary">
      {insight?.personalized
        ? t('step4.ai.personalizedFrom', { messages: insight.styleExamplesUsed })
        : t('step4.ai.basicFallback')}
    </span>
  );
}

function AiSuggestionBubble({
  text,
  insight,
}: {
  text: string;
  insight: OnboardingAiDemoInsight | null;
}) {
  const t = useT('onboarding');
  return (
    <div className="flex flex-col items-end gap-1.5" data-region="ai-suggestion">
      <span className="flex items-center gap-1 text-2xs font-bold uppercase tracking-wider text-accent">
        <Sparkles style={{ width: 11, height: 11 }} aria-hidden />
        {t('step4.ai.suggestionEyebrow')}
      </span>
      <div className="max-w-[82%] rounded-md rounded-br-sm border border-dashed border-accent-border bg-accent-dim px-3 py-2 text-xs leading-relaxed text-primary">
        {text}
      </div>
      <PersonalizationNote insight={insight} />
      <span className="text-2xs text-tertiary">{t('step4.ai.previewOnly')}</span>
    </div>
  );
}

function AutoSentBubble({
  text,
  insight,
}: {
  text: string;
  insight: OnboardingAiDemoInsight | null;
}) {
  const t = useT('onboarding');
  return (
    <div className="flex flex-col items-end gap-1.5" data-region="ai-auto">
      <span className="flex items-center gap-1 text-2xs font-bold uppercase tracking-wider text-success">
        {t('step4.ai.autoEyebrow')}
      </span>
      <div className="max-w-[82%] rounded-md rounded-br-sm border border-accent bg-accent px-3 py-2 text-xs leading-relaxed text-on-accent">
        {text}
      </div>
      <PersonalizationNote insight={insight} />
      <span className="text-2xs text-tertiary">{t('step4.ai.previewOnly')}</span>
    </div>
  );
}

function EmptyDemo() {
  const t = useT('onboarding');
  return (
    <Card padding="lg" radius="xl" data-region="onboarding-demo-empty">
      <div className="flex flex-col items-center gap-3 text-center">
        <Sparkles className="text-accent" style={{ width: 28, height: 28 }} aria-hidden />
        <span className="text-sm font-bold text-primary">{t('step4.empty.title')}</span>
        <span className="max-w-sm text-xs leading-relaxed text-secondary">
          {t('step4.empty.body')}
        </span>
        <span className="rounded-md border border-accent-border bg-accent-dim px-3 py-1.5 text-2xs text-primary">
          {t('step4.empty.hint')}
        </span>
      </div>
    </Card>
  );
}

function FailedDemo({
  reason,
  onRetry,
  retrying = false,
}: {
  reason: string;
  onRetry: () => void;
  retrying?: boolean;
}) {
  const t = useT('onboarding');
  return (
    <Card padding="lg" radius="xl" data-region="onboarding-demo-failed">
      <div className="flex flex-col items-center gap-3 text-center">
        <RefreshCw className="text-warning" style={{ width: 28, height: 28 }} aria-hidden />
        <span className="text-sm font-bold text-primary">{t('step4.failed.title')}</span>
        <span className="max-w-sm text-xs leading-relaxed text-secondary">{reason}</span>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry} loading={retrying}>
          {t('step4.failed.retry')}
        </Button>
      </div>
    </Card>
  );
}
